import React, { useState } from "react";
import {
    Prospect,
    ProspectDraft,
    checkPhoneType,
    createProspect,
    deleteProspectScreenshot,
    refreshProspectListingPreview,
    updateProspect,
    uploadProspectScreenshot,
} from "../../api/ProspectAPI";
import { logApiError, getUserMessage } from "../../api/apiError";
import { autocompleteCategories } from "../../api/CategoryAPI";
import CategoryTreeNavigator from "../category/CategoryTreeNavigator";
import GeoAreaPicker from "../geo/GeoAreaPicker";
import { fillWhyTemplate, renderWhyPoints } from "../WhyCategoryPage";
import type { CoverageArea } from "../../utils/coverage";

const EMPTY_DRAFT: ProspectDraft = {
    name: "",
    orgName: "",
    email: "",
    phone: "",
    domain: "",
    contactUrl: "",
    category: "",
    countryId: null,
    admin1Id: null,
    admin2Id: null,
    cityId: null,
    location: "",
    notes: "",
    currentListingUrl: "",
    whyHeading: "",
    whyPoints: "",
};

interface Props {
    token: string;
    /** null = create a new prospect; a Prospect = edit it. */
    editing: Prospect | null;
    /** Called when the user leaves the form; saved tells the parent to reload. */
    onDone: (saved: boolean) => void;
}

/** Full-panel create/edit form for one prospect — shown exclusively (the
 *  Outreach list is hidden while it's open) to keep both screens simple. */
const ProspectForm: React.FC<Props> = ({ token, editing, onDone }) => {
    const [draft, setDraft] = useState<ProspectDraft>(editing ? {
        name: editing.name ?? "",
        orgName: editing.orgName ?? "",
        email: editing.email ?? "",
        phone: editing.phone ?? "",
        domain: editing.domain ?? "",
        contactUrl: editing.contactUrl ?? "",
        category: editing.category ?? "",
        countryId: editing.countryId,
        admin1Id: editing.admin1Id,
        admin2Id: editing.admin2Id,
        cityId: editing.cityId,
        location: editing.location ?? "",
        notes: editing.notes ?? "",
        currentListingUrl: editing.currentListingUrl ?? "",
        whyHeading: editing.whyHeading ?? "",
        whyPoints: editing.whyPoints ?? "",
    } : EMPTY_DRAFT);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [categorySuggestions, setCategorySuggestions] = useState<string[]>([]);
    const [categoryTreeOpen, setCategoryTreeOpen] = useState(false);
    // Ad-hoc line-type check ("can this number receive texts?") — the save
    // stores the result on the prospect automatically.
    const [phoneChecking, setPhoneChecking] = useState(false);
    const [phoneInfo, setPhoneInfo] = useState<string | null>(null);
    // Their current listing: the link (og preview fetched server-side) and a
    // screenshot the admin takes coexist — the why page prefers the
    // screenshot, the link powers {post_link} and the click-through. The
    // file is held locally and uploaded right after save — so it works on
    // the create form too.
    const [hasScreenshot, setHasScreenshot] = useState(!!editing?.hasScreenshot);
    const [pendingScreenshot, setPendingScreenshot] = useState<File | null>(null);
    const [shotBusy, setShotBusy] = useState(false);
    const [shotMsg, setShotMsg] = useState<string | null>(null);

    const onScreenshotRemove = async () => {
        if (!editing) return;
        setShotBusy(true);
        setShotMsg(null);
        try {
            await deleteProspectScreenshot(token, editing.id);
            setHasScreenshot(false);
            setShotMsg("Screenshot removed — the why page falls back to the fetched preview.");
        } catch (e) {
            logApiError(e);
            setShotMsg(getUserMessage(e));
        } finally {
            setShotBusy(false);
        }
    };

    // A failed screenshot upload must not duplicate the prospect on retry:
    // remember the created id and only re-run the update + upload.
    const [createdId, setCreatedId] = useState<number | null>(null);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            let id = editing?.id ?? createdId;
            if (id == null) {
                const created = await createProspect(token, draft);
                id = created.id;
                setCreatedId(id);
            } else {
                await updateProspect(token, id, draft);
            }
            if (pendingScreenshot) {
                try {
                    await uploadProspectScreenshot(token, id, pendingScreenshot);
                } catch (e) {
                    logApiError(e);
                    setError("Prospect saved, but the screenshot upload failed — "
                        + getUserMessage(e) + " Fix the file and press Save again.");
                    return; // stay on the form; Save retries just update+upload
                }
            }
            onDone(true);
        } catch (e) {
            logApiError(e);
            setError(getUserMessage(e));
        } finally {
            setBusy(false);
        }
    };

    // Location from the shared cascading picker. Empty chains are ignored so
    // opening the Edit form doesn't wipe an existing location.
    const onGeoChange = (chain: CoverageArea[]) => {
        const levels = chain.filter((c) => c.level !== "PostalCode");
        if (levels.length === 0) return;
        const idOf = (lvl: string) => {
            const hit = levels.find((c) => c.level === lvl);
            return hit ? Number(hit.id) : null;
        };
        setDraft((d) => ({
            ...d,
            countryId: idOf("Country"),
            admin1Id: idOf("Admin1"),
            admin2Id: idOf("Admin2"),
            cityId: idOf("City"),
            location: [...levels].reverse().map((c) => c.name).join(", "),
        }));
    };

    const onCategoryInput = (value: string) => {
        setDraft((d) => ({ ...d, category: value }));
        if (value.trim().length < 2) { setCategorySuggestions([]); return; }
        autocompleteCategories(token, value.trim())
            .then((hits) => setCategorySuggestions(hits.map((h) => h.name)))
            .catch(() => setCategorySuggestions([]));
    };

    const field = (label: string, key: keyof ProspectDraft, placeholder = "") => (
        <div className="form-group" style={{ marginBottom: "0.5rem" }}>
            <label>{label}</label>
            <input
                value={(draft[key] as string) ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                placeholder={placeholder}
                disabled={busy}
            />
        </div>
    );

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
                <button className="btn-secondary" onClick={() => onDone(false)} disabled={busy}>
                    ← Back to Outreach
                </button>
                <h2 style={{ margin: 0 }}>{editing ? "Edit prospect" : "New prospect"}</h2>
                {editing?.whyToken && (
                    <a href={`/why/p/${editing.whyToken}`} target="_blank" rel="noopener noreferrer"
                       title="Their personalized comparison page — what {why_link} becomes in invite emails">
                        View their why page ↗
                    </a>
                )}
            </div>

            {error && <div className="error-message" style={{ marginBottom: "0.5rem" }}>{error}</div>}

            <div style={{ maxWidth: "560px" }}>
                {field("Contact name", "name", "Person to address")}
                {field("Organization", "orgName")}
                {/* Reach & web: how we contact them and where they live online. */}
                <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem" }}>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: "0.4rem" }}>Contact &amp; web</div>
                {field("Email", "email", "Used to send the invite and detect joining")}
                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <label>Phone</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                        <input
                            value={draft.phone ?? ""}
                            onChange={(e) => { setDraft((d) => ({ ...d, phone: e.target.value })); setPhoneInfo(null); }}
                            disabled={busy}
                            style={{ flex: 1, minWidth: "12rem" }}
                        />
                        {(draft.phone ?? "").trim() !== "" && (
                            <button
                                type="button"
                                className="btn-sm"
                                title="Check whether this number can receive SMS (mobile vs landline)"
                                disabled={busy || phoneChecking}
                                onClick={async () => {
                                    setPhoneChecking(true);
                                    setPhoneInfo(null);
                                    try {
                                        const info = await checkPhoneType(token, (draft.phone ?? "").trim());
                                        setPhoneInfo(`${info.phoneType}${info.carrier ? " — " + info.carrier : ""}`);
                                    } catch (e) {
                                        logApiError(e);
                                        setPhoneInfo(getUserMessage(e));
                                    } finally {
                                        setPhoneChecking(false);
                                    }
                                }}
                            >
                                {phoneChecking ? "Checking…" : "Check number"}
                            </button>
                        )}
                    </div>
                    {(phoneInfo ?? (editing?.phoneType
                        ? `${editing.phoneType}${editing.phoneCarrier ? " — " + editing.phoneCarrier : ""}` : null)) && (
                        <div style={{ fontSize: "0.85rem", marginTop: "0.25rem" }}>
                            {(() => {
                                const text = phoneInfo ?? `${editing!.phoneType}${editing!.phoneCarrier ? " — " + editing!.phoneCarrier : ""}`;
                                const bad = text.startsWith("LANDLINE") || text.startsWith("INVALID") || text.startsWith("UNKNOWN");
                                return (
                                    <span style={{ color: bad ? "#b45309" : "var(--success, #2e7d32)", fontWeight: 600 }}>
                                        {text}{text.startsWith("LANDLINE") ? " — cannot receive SMS" : ""}
                                    </span>
                                );
                            })()}
                        </div>
                    )}
                </div>
                {field("Domain", "domain", "e.g. actionis.com — their web domain (optional)")}
                {field("Contact page URL", "contactUrl", "e.g. https://www.actionis.com/contacts (optional)")}
                </div>
                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <label>Category (required)</label>
                    <input
                        value={draft.category ?? ""}
                        onChange={(e) => onCategoryInput(e.target.value)}
                        placeholder="Start typing to pick a category — used in the invite text"
                        list="prospect-category-suggestions"
                        disabled={busy}
                    />
                    <datalist id="prospect-category-suggestions">
                        {categorySuggestions.map((name) => (
                            <option key={name} value={name} />
                        ))}
                    </datalist>
                    <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => setCategoryTreeOpen((o) => !o)}
                        disabled={busy}
                        style={{ marginTop: "0.35rem" }}
                    >
                        {categoryTreeOpen ? "Hide category tree" : "🌳 Browse the category tree"}
                    </button>
                    {categoryTreeOpen && (
                        <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.5rem", marginTop: "0.35rem" }}>
                            <CategoryTreeNavigator
                                token={token}
                                onSelectLeaf={(leaf) => {
                                    // A tree pick guarantees the exact name, so the
                                    // backend category resolution always matches.
                                    setDraft((d) => ({ ...d, category: leaf.name }));
                                    setCategoryTreeOpen(false);
                                }}
                            />
                        </div>
                    )}
                </div>
                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <label>Location (optional — leave empty for an online/national business; country-only means national)</label>
                    {draft.location && (
                        <div className="item-description" style={{ marginBottom: "0.25rem" }}>
                            Current: {draft.location}
                        </div>
                    )}
                    <GeoAreaPicker token={token} onChange={onGeoChange} />
                </div>
                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <label>Their current listing (shown on their “why BabaHanks” page)</label>
                    <div className="item-description" style={{ marginBottom: "0.35rem" }}>
                        Their post appears on their why page, and the link also fills
                        {" {post_link}"} in invite messages. Provide both when you can:
                        the screenshot guarantees the picture (and prints well), the
                        link gives the click-through. Either alone works too.
                    </div>
                    <div style={{ marginBottom: "0.4rem" }}>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.2rem" }}>
                            Link to their post or website
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                            <input
                                value={draft.currentListingUrl ?? ""}
                                onChange={(e) => setDraft((d) => ({ ...d, currentListingUrl: e.target.value }))}
                                placeholder="e.g. https://www.craigslist.org/view/d/…"
                                disabled={busy}
                                style={{ flex: 1, minWidth: "16rem" }}
                            />
                            {editing && (draft.currentListingUrl ?? "").trim() !== "" && (
                                <button
                                    type="button"
                                    className="btn-sm"
                                    title="Fetch the post's preview (image, title, description) again — the fetch is otherwise skipped when the URL hasn't changed"
                                    disabled={busy || shotBusy}
                                    onClick={async () => {
                                        setShotBusy(true);
                                        setShotMsg(null);
                                        try {
                                            const updated = await refreshProspectListingPreview(token, editing.id);
                                            setShotMsg(updated.currentListingImageUrl || updated.currentListingTitle
                                                ? "Preview refreshed — reload the why page to see it."
                                                : "Fetched, but the site returned no preview (it may block bots) — consider a screenshot instead.");
                                        } catch (e) {
                                            logApiError(e);
                                            setShotMsg(getUserMessage(e));
                                        } finally {
                                            setShotBusy(false);
                                        }
                                    }}
                                >
                                    ⟳ Refresh preview
                                </button>
                            )}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.2rem" }}>
                            Screenshot of their post (the why page prefers it over the link's preview)
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                            {hasScreenshot && !pendingScreenshot && (
                                <span style={{ fontSize: "0.85rem", color: "var(--success, #2e7d32)", fontWeight: 600 }}>
                                    ✓ screenshot on file
                                </span>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setPendingScreenshot(e.target.files?.[0] ?? null)}
                                disabled={busy || shotBusy}
                            />
                            {pendingScreenshot && (
                                <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                                    uploads when you {editing ? "save" : "add the prospect"}
                                </span>
                            )}
                            {hasScreenshot && editing && (
                                <button type="button" className="btn-sm" onClick={onScreenshotRemove} disabled={busy || shotBusy}>
                                    Remove
                                </button>
                            )}
                        </div>
                    </div>
                    {shotMsg && (
                        <div className={/^(Screenshot|Preview|Fetched)/.test(shotMsg) ? "success-message" : "error-message"} style={{ marginTop: "0.25rem" }}>
                            {shotMsg}
                        </div>
                    )}
                </div>
                <details style={{ marginBottom: "0.5rem", border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.5rem 0.75rem" }}>
                    <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                        Customize their why page (optional)
                        {(draft.whyHeading?.trim() || draft.whyPoints?.trim()) ? " — customized ✓" : ""}
                    </summary>
                    <div className="item-description" style={{ margin: "0.5rem 0" }}>
                        Overrides the global why-page texts for THIS prospect only —
                        their page keeps this wording even if the templates change
                        later. Blank = use the global templates. Same placeholders
                        ({"{category}"}, {"{company}"}, {"{key_details}"}) and HTML
                        support.
                    </div>
                    <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                        <label>Heading</label>
                        <input
                            value={draft.whyHeading ?? ""}
                            onChange={(e) => setDraft((d) => ({ ...d, whyHeading: e.target.value }))}
                            placeholder="blank = global template"
                            disabled={busy}
                        />
                    </div>
                    <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                        <label>Pain points (HTML or one bullet per line)</label>
                        <textarea
                            rows={6}
                            value={draft.whyPoints ?? ""}
                            onChange={(e) => setDraft((d) => ({ ...d, whyPoints: e.target.value }))}
                            placeholder="blank = global template"
                            disabled={busy}
                        />
                    </div>
                    {(draft.whyHeading?.trim() || draft.whyPoints?.trim()) && (
                        <div style={{ border: "1px solid var(--border-color, #eee)", borderRadius: "6px", background: "var(--bg-primary, #fff)", padding: "0.75rem" }}>
                            <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginBottom: "0.4rem" }}>
                                Live preview (placeholders filled with this prospect's values):
                            </div>
                            {draft.whyHeading?.trim() && (
                                <h3 style={{ textAlign: "center", margin: "0 0 0.5rem" }}>
                                    {fillWhyTemplate(draft.whyHeading, { category: draft.category, company: draft.orgName })}
                                </h3>
                            )}
                            {draft.whyPoints?.trim() && (
                                <div style={{ textAlign: "center" }}>
                                    {renderWhyPoints(draft.whyPoints, "price, features, options")}
                                </div>
                            )}
                        </div>
                    )}
                </details>
                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <label>Notes</label>
                    <textarea
                        rows={2}
                        value={draft.notes ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                        disabled={busy}
                    />
                </div>
                {(() => {
                    // Name exactly what still blocks saving — a disabled button
                    // with a hover tooltip hides the reason. Location is NOT
                    // required (empty = online/national business).
                    const missing: string[] = [];
                    if (!(draft.name?.trim() || draft.orgName?.trim() || draft.email?.trim())) {
                        missing.push("a name, org name, or email (any one)");
                    }
                    if (!draft.category?.trim()) missing.push("Category");
                    return (
                        <div className="button-group" style={{ alignItems: "center", flexWrap: "wrap" }}>
                            <button onClick={submit} disabled={busy || missing.length > 0}>
                                {busy ? "Saving…" : editing ? "Save" : "Add prospect"}
                            </button>
                            <button className="btn-secondary" onClick={() => onDone(false)} disabled={busy}>
                                Cancel
                            </button>
                            {missing.length > 0 && (
                                <span style={{ fontSize: "0.85rem", color: "#b45309", fontWeight: 600 }}>
                                    Still missing: {missing.join(", ")}
                                </span>
                            )}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default ProspectForm;